import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import Loader from "../Loader";
import { firebaseAuth, firestoreOperationsDb } from "../../firebase";
import { getConsentStatus } from "../../services/consentService";
import "./seniorsAtHome.css";

const DASHBOARD_PATH = "/seniorsathome/dashboard";


const resolveAuthUser = () =>
  new Promise((resolve) => {
    if (!firebaseAuth || typeof firebaseAuth.onAuthStateChanged !== "function") {
      resolve((firebaseAuth && firebaseAuth.currentUser) || null);
      return;
    }
    if (firebaseAuth.currentUser) {
      resolve(firebaseAuth.currentUser);
      return;
    }

    let settled = false;
    let unsubscribe = null;
    const finish = (user) => {
      if (settled) return;
      settled = true;
      if (typeof unsubscribe === "function") unsubscribe();
      resolve(user || null);
    };

    unsubscribe = firebaseAuth.onAuthStateChanged((user) => finish(user));
    setTimeout(() => finish(firebaseAuth.currentUser || null), 3000);
  });

const SeniorsAtHomeRegistrationGuard = ({
  guardedComponent: GuardedComponent,
  ...props
}) => {
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      setStatus("loading");
      setError("");

      const authUser = await resolveAuthUser();
      if (cancelled) return;

      if (!authUser || !authUser.uid) {
        setStatus("allowed");
        return;
      }

      try {
        const userSnap = await firestoreOperationsDb
          .collection("users")
          .doc(authUser.uid)
          .get();
        if (cancelled) return;

        if (!userSnap || !userSnap.exists) {
          setStatus("allowed");
          return;
        }

        const userData = userSnap.data() || {};
        if (userData.isCarerMode !== true) {
          setStatus("allowed");
          return;
        }

        const consentStatus = await getConsentStatus();
        if (cancelled) return;
        if (consentStatus && consentStatus.complete === true) {
          setStatus("complete");
          return;
        }

        setStatus("allowed");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setError(
          (err && err.message) ||
            "Unable to verify your registration status."
        );
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (status === "loading") {
    return (
      <div className="sah-guard-state" role="status" aria-live="polite">
        <Loader />
        <p className="sah-guard-title">Checking your registration status…</p>
      </div>
    );
  }

  if (status === "complete") {
    return <Redirect to={DASHBOARD_PATH} />;
  }

  if (status === "error") {
    return (
      <div className="sah-guard-state" role="alert">
        <p className="sah-guard-title">
          We couldn&apos;t verify your registration status.
        </p>
        <p className="sah-guard-text">{error}</p>
        <button
          type="button"
          className="sah-bottom-btn sah-bottom-btn--review"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!GuardedComponent) return null;
  return <GuardedComponent {...props} />;
};

export default SeniorsAtHomeRegistrationGuard;
